import {
  AbstractCurrentSubscriptionModel,
  PendingOrder,
} from '../../api/billing-types';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import AvatarChip from '../../shared/avatar-chip';
import { CheckoutModelSchema } from './billing-form-page';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

interface AddLicensesStepProps {
  control: Control<CheckoutModelSchema>;
  subscription: AbstractCurrentSubscriptionModel;
  watch: UseFormWatch<CheckoutModelSchema>;
  setValue: UseFormSetValue<CheckoutModelSchema>;
  errors: FieldErrors<CheckoutModelSchema>;
  pendingOrders: (PendingOrder | undefined)[];
  bundledGpanelSubscription: AbstractCurrentSubscriptionModel | null;
  totalExistingWorkspaceLicenses: number;
}

export const AddLicensesStep = ({
  control,
  subscription,
  watch,
  setValue,
  errors,
  pendingOrders,
  bundledGpanelSubscription,
  totalExistingWorkspaceLicenses,
}: AddLicensesStepProps) => {
  const items = watch('items');
  const hasMoreThanOneLineItem = items.length > 1;
  const qty = items[0].qty || 0;

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 500 }}>
        Add {subscription.productModel.productName}
      </Typography>
      <Stack flexDirection="column" spacing={1} gap={1}>
        <Stack direction="row" alignItems="baseline" gap={0.5} spacing={0.5}>
          <Typography>Payment Plan: </Typography>
          <Typography sx={{ fontWeight: 600 }}>
            {subscription.billingPlan}
          </Typography>
          <Typography sx={{ fontWeight: 'bold', fontSize: '2rem' }}>
            {new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
            }).format(subscription.pricePerUnit)}
          </Typography>
          <Typography>{subscription.costPerString}</Typography>
        </Stack>
        <Stack direction="row" sx={{ pl: 1 }} gap={0.5}>
          <Typography
            dangerouslySetInnerHTML={{
              __html: subscription.paymentPlanBulletPoints,
            }}
          />
        </Stack>
        {pendingOrders && pendingOrders.length > 0 ? (
          <>
            <Divider orientation="vertical" flexItem />
            <Stack gap={1} pb={1}>
              {pendingOrders?.map((pendingOrder) => {
                return pendingOrder ? (
                  <Alert
                    sx={{ width: 'fit-content', ml: 2 }}
                    key={pendingOrder.key}
                    severity="info"
                  >
                    Pending Order #{pendingOrder.orderNumber}:{' '}
                    <AvatarChip email={pendingOrder.purchasedBy} type="User" />{' '}
                    placed an order for {pendingOrder.licenseCount}{' '}
                    {pendingOrder.licenseCount > 1
                      ? 'additional licenses'
                      : 'additional license'}{' '}
                    on {new Date(pendingOrder.orderDate).toLocaleDateString()}.
                  </Alert>
                ) : null;
              })}
            </Stack>
          </>
        ) : null}
      </Stack>

      <Card sx={{ mt: 2 }}>
        <CardContent>
          <Stack flexDirection={'row'} gap={2}>
            <Stack sx={{ width: '100%' }}>
              <Stack direction="column" mt={1} ml={2} gap={0.5}>
                <Typography sx={{ fontWeight: 600 }}>
                  {subscription.totalPurchasedLicenses} current licenses
                </Typography>
                <Stack direction="row" alignItems="center" mt={2} spacing={2}>
                  <Box sx={{ width: 210 }}>
                    <Controller
                      name={'items.0.qty'}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          autoFocus
                          value={field.value}
                          label="Additional Licenses"
                          fullWidth
                          slotProps={{
                            htmlInput: {
                              min: 0,
                            },
                          }}
                          autoComplete="off"
                          type={'number'}
                          onChange={(e) => {
                            if (e.target.value === '') {
                              field.onChange('');
                              return;
                            }

                            let gPanelLicensesToAdd = Number(e.target.value);
                            field.onChange(gPanelLicensesToAdd);
                            if (
                              bundledGpanelSubscription &&
                              hasMoreThanOneLineItem
                            ) {
                              if (
                                bundledGpanelSubscription.totalPurchasedLicenses >
                                totalExistingWorkspaceLicenses
                              ) {
                                gPanelLicensesToAdd =
                                  gPanelLicensesToAdd -
                                  (bundledGpanelSubscription.totalPurchasedLicenses -
                                    totalExistingWorkspaceLicenses);
                              }

                              if (gPanelLicensesToAdd > 0) {
                                setValue('items.1.qty', gPanelLicensesToAdd);
                              }
                            }
                          }}
                          error={!!errors.items?.[0]?.qty}
                          sx={{ width: 210 }}
                        />
                      )}
                    />
                  </Box>

                  <Typography sx={{ ml: 1 }}>
                    <span>
                      {`${qty} license${
                        qty > 1 ? 's' : ''
                      } x ${new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD',
                      }).format(subscription.proratedPricePerUnit)}`}
                    </span>
                  </Typography>
                </Stack>
              </Stack>
              <Divider sx={{ my: 2 }} />
              <Stack direction="column" mt={1} spacing={0.5} ml={2}>
                <Typography sx={{ ml: 2, fontWeight: 600 }}>
                  <span>
                    {`${
                      Number(subscription.totalPurchasedLicenses) + Number(qty)
                    } total licenses`}
                  </span>
                </Typography>
                <Stack
                  direction="row"
                  gap={2}
                  alignItems="center"
                  sx={{ ml: 2 }}
                >
                  <Typography>
                    <span>
                      {new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD',
                      }).format(qty * subscription.proratedPricePerUnit)}
                    </span>
                  </Typography>
                  <Typography>{subscription.estimatedChargeText}</Typography>
                  <Typography>(Pay on your next bill date)</Typography>
                </Stack>
                {subscription.billingType === 'monthlyOnBillDate' ||
                subscription.billingType === 'monthlyPostPay' ||
                subscription.billingType === 'monthlyPrepay' ? (
                  <Typography sx={{ ml: 2 }}>
                    <span>
                      {`Pro-rated at  ${new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD',
                      }).format(
                        subscription.proratedPricePerUnit
                      )} each, based on your next bill date of ${new Date(
                        subscription.monthlyBillDate || subscription.startTime
                      ).toLocaleDateString('en-US', {
                        month: '2-digit',
                        day: '2-digit',
                        year: 'numeric',
                      })}`}
                    </span>
                  </Typography>
                ) : (
                  <Typography sx={{ ml: 2 }}>
                    <span>
                      {`Pro-rated for your remaining contract term: ${new Date(
                        subscription.startTime
                      ).toLocaleDateString('en-US', {
                        month: '2-digit',
                        day: '2-digit',
                        year: 'numeric',
                      })} - ${new Date(subscription.timeout).toLocaleDateString(
                        'en-US',
                        {
                          month: '2-digit',
                          day: '2-digit',
                          year: 'numeric',
                        }
                      )}`}
                    </span>
                  </Typography>
                )}
              </Stack>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};
